import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  TextField,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Snackbar,
  useTheme,
  Grid
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { CREATE_INQUIRY } from '../../graphql/mutations';
import { validateInquiry } from '../../utils/validationUtils';

const InquiryPage: React.FC = () => {
  const theme = useTheme();
  
  // GraphQL Mutation
  const [createInquiry] = useMutation(CREATE_INQUIRY);
  
  // フォームの状態
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    inquiryType: 'ロープレ実施',
    message: ''
  });
  
  // 送信状態
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  
  // バリデーションエラー状態
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
  
  // フォーム入力の変更ハンドラ
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 入力時にそのフィールドのエラーをクリア
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  // ラジオボタンの変更ハンドラ
  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      inquiryType: e.target.value
    }));
  };
  
  // フォーム送信ハンドラ
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // バリデーション実行
    const validationResult = validateInquiry({
      name: formData.name,
      email: formData.email,
      message: formData.message
    });
    
    if (!validationResult.isValid) {
      // バリデーションエラーを状態に設定
      const errors: { [key: string]: string } = {};
      validationResult.errors.forEach(error => {
        errors[error.field] = error.message;
      });
      setValidationErrors(errors);
      
      // 最初のエラーメッセージを表示
      setSubmitError(validationResult.errors[0].message);
      return;
    }
    
    // バリデーションが成功した場合、エラーをクリア
    setValidationErrors({});
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // GraphQL Mutationを実行
      const result = await createInquiry({
        variables: {
          input: {
            category: formData.inquiryType,
            contactName: formData.name,
            contactEmail: formData.email,
            contactCompany: formData.company || null,
            contactBody: formData.message || null,
          }
        }
      });

      if (result.data?.createInquiry) {
        setIsSubmitting(false);
        setSubmitSuccess(true);
        setValidationErrors({});
        setFormData({
          name: '',
          email: '',
          company: '',
          inquiryType: 'ロープレ実施',
          message: ''
        });
      } else {
        throw new Error('問合せの作成に失敗しました。');
      }
    } catch (error) {
      setIsSubmitting(false);
      setSubmitError('送信中にエラーが発生しました。後でもう一度お試しください。');
      console.error('Inquiry submission error:', error);
    }
  };
  
  // ページ表示時に画面最上部までスクロール
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // スナックバーを閉じる
  const handleCloseSnackbar = () => {
    setSubmitSuccess(false);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper 
        elevation={0}
        sx={{ 
          p: { xs: 2, sm: 4 }, 
          borderRadius: '12px',
          backgroundColor: theme.palette.background.paper,
          mb: 4
        }}
      >
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            mb: 4, 
            fontWeight: 600,
            color: theme.palette.smartSkillTalk.linkBlue,
            // textAlign: 'center'
          }}
        >
          お問い合わせフォーム
        </Typography>
        
        <Typography 
          sx={{ 
            mb: 4,
            color: theme.palette.smartSkillTalk.textDark,
            // textAlign: 'center',
            fontSize: '15px',
            lineHeight: 1.6
          }}
        >
<div>平素より当サービスをご利用いただきありがとうございます。お問い合わせの前に、以下の内容をご確認ください。</div>
<div>
  <Button
    component={RouterLink}
    to="/help/faq"
    variant="text"
    sx={{
      color: theme.palette.smartSkillTalk.linkBlue,
      textDecoration: 'underline',
      '&:hover': {
        backgroundColor: 'transparent',
        textDecoration: 'underline',
      }
    }}
  >
    ・よくある質問（FAQ）
  </Button>
  <Button
    component={RouterLink}
    to="/help/faq"
    variant="text"
    sx={{
      color: theme.palette.smartSkillTalk.linkBlue,
      textDecoration: 'underline',
      '&:hover': {
        backgroundColor: 'transparent',
        textDecoration: 'underline',
      }
    }}
  >
    ・操作マニュアル（PDF）
  </Button>
</div>
<div>FAQ・マニュアルで解決しない場合は、以下のフォームよりお問い合わせください。<br />
  内容を確認後、担当者より順次ご連絡いたします。</div>
        </Typography>
        
        {submitError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {submitError}
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="name"
                name="name"
                label="お名前"
                value={formData.name}
                onChange={handleChange}
                error={!!validationErrors.name}
                helperText={validationErrors.name}
                sx={{ mb: { xs: 2, sm: 0 } }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="company"
                name="company"
                label="企業コード"
                value={formData.company}
                onChange={handleChange}
                inputProps={{
                  inputMode: 'url' 
                }}                
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="email"
                name="email"
                label="メールアドレス"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={!!validationErrors.email}
                helperText={validationErrors.email}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" sx={{ 
                  color: theme.palette.smartSkillTalk.textDark,
                  mb: 1,
                  '&.Mui-focused': {
                    color: theme.palette.smartSkillTalk.textDark
                  }
                }}>
                  お問い合わせ種類
                </FormLabel>
                <RadioGroup
                  row
                  name="inquiryType"
                  value={formData.inquiryType}
                  onChange={handleRadioChange}
                  sx={{ 
                    flexWrap: 'wrap',
                    '& .MuiFormControlLabel-root': {
                      mr: 4,
                      mb: 1
                    }
                  }}
                >
                  <FormControlLabel 
                    value="ロープレ実施" 
                    control={<Radio />} 
                    label="ロープレ実施" 
                  />
                  <FormControlLabel 
                    value="管理メニュー" 
                    control={<Radio />} 
                    label="管理メニュー" 
                  />
                  <FormControlLabel 
                    value="ご意見・ご要望" 
                    control={<Radio />} 
                    label="ご意見・ご要望" 
                  />
                  <FormControlLabel 
                    value="その他" 
                    control={<Radio />} 
                    label="その他" 
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="message"
                name="message"
                label="お問い合わせ内容"
                multiline
                rows={6}
                value={formData.message}
                onChange={handleChange}
                error={!!validationErrors.message}
                helperText={validationErrors.message}
                inputProps={{
                  inputMode: 'text' 
                }}
              />
            </Grid>
          </Grid>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={isSubmitting}
              startIcon={<SendIcon />}
              sx={{
                py: 1.5,
                px: 5,
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 600,
                backgroundColor: theme.palette.smartSkillTalk.linkBlue,
                color: '#fff',
                '&:hover': {
                  backgroundColor: '#3687DF',
                }
              }}
            >
              {isSubmitting ? '送信中...' : '送信する'}
            </Button>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              component={RouterLink}
              to="/help/faq"
              variant="text"
              sx={{
                color: theme.palette.smartSkillTalk.linkBlue,
                textDecoration: 'underline',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                }
              }}
            >
              FAQページに戻る
            </Button>
          </Box>
        </Box>
      </Paper>
      
      <Snackbar
        open={submitSuccess}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          お問い合わせを送信しました。ご連絡ありがとうございます。
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default InquiryPage;
